import type { RasiTechSettings } from '../global/types/sharedState';
import { DEFAULT_RASITECH_SETTINGS } from '../global/types/sharedState';
import { canUsePaidModFeature } from './rasitechAccess';
import { safeSessionStorageRemove } from './safeStorage';

export type ModFeature = keyof RasiTechSettings;

export { DEFAULT_RASITECH_SETTINGS };

const RASITECH_STORAGE_KEY = 'rasiTechSettings';
export const RASITECH_BOT_AUTH_TOKEN_KEY = 'rasiTechBotAuthToken';
export const RASITECH_BOT_SESSION_MARKER_KEY = 'rasiTechBotSession';
/** Set when user opens bot token screen without restarting API (still on QR/phone auth underneath). */
export const RASITECH_BOT_UI_ONLY_KEY = 'rasiTechBotUiOnly';

export function clearBotAuthSessionKeys() {
  safeSessionStorageRemove(RASITECH_BOT_AUTH_TOKEN_KEY);
  safeSessionStorageRemove(RASITECH_BOT_SESSION_MARKER_KEY);
  safeSessionStorageRemove(RASITECH_BOT_UI_ONLY_KEY);
}

let cachedSettings: RasiTechSettings = loadStoredSettings();

function loadStoredSettings(): RasiTechSettings {
  if (typeof localStorage === 'undefined') {
    return { ...DEFAULT_RASITECH_SETTINGS };
  }

  try {
    const stored = localStorage.getItem(RASITECH_STORAGE_KEY);
    if (stored) {
      return {
        ...DEFAULT_RASITECH_SETTINGS,
        ...JSON.parse(stored) as Partial<RasiTechSettings>,
      };
    }
  } catch {
    // Ignore corrupt storage
  }

  return { ...DEFAULT_RASITECH_SETTINGS };
}

function persistSettings() {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(RASITECH_STORAGE_KEY, JSON.stringify(cachedSettings));
}

export function setModFeatureCache(settings: Partial<RasiTechSettings>) {
  cachedSettings = {
    ...cachedSettings,
    ...settings,
  };
  persistSettings();
}

export function isModFeatureEnabled(feature: ModFeature): boolean {
  if (!cachedSettings[feature]) return false;
  return canUsePaidModFeature(feature);
}

export function getModFeatureSettings(): RasiTechSettings {
  return { ...cachedSettings };
}
